import type { ToastInterface } from "vue-toastification";
import type { InvitationDisposition } from "@/api/index.d";
import { calculateTimeout } from "@/helpers/utilities";

/**
 * LSP-19707: turn the API's per-channel invitation skip reasons into agent-facing copy.
 *
 * LSP-19063 gave the caller a reason an invitation was suppressed, but Resend Invitation acted on
 * exactly one value of it (`participantSmsNotificationOff`). Every other reason fell through to the
 * green "The invitation has been re-sent to the contact." toast, so the agent was told a message went
 * out when it did not — the same false reassurance LSP-19063 set out to remove, reached through a
 * different door. `emailReason` was never read at all, so a suppressed email invitation was invisible
 * on that screen no matter what the API reported.
 *
 * Wording and structure follow the author's direction on the ticket (comment 151656):
 * - any skipped channel suppresses the success toast;
 * - a warning headed "Invitation could not be sent", one bullet per blocked channel;
 * - a partial send says so explicitly ("Email sent. Text message could not be sent.") rather than
 *   reading as a full success;
 * - every bullet names what the agent should DO, not the API's reason code.
 *
 * Reason vocabularies are produced by contact-api and must stay in sync with:
 * - SMS:   `getSmsInvitationSkipReasons` in `apps/contact-api/src/helpers/invitationLogging.ts`
 * - Email: `getEmailInvitationSkipReasons` in `apps/contact-api/src/helpers/invitationLogging.ts`
 *
 * An unrecognised reason is deliberately NOT swallowed: it still produces a warning, because "we do
 * not have copy for this reason yet" must never read as "the message was sent".
 */

export type InvitationChannel = "sms" | "email";

const CHANNEL_LABELS: Record<InvitationChannel, string> = {
	sms: "Text message",
	email: "Email"
};

const SMS_SKIP_MESSAGES: Record<string, string> = {
	participantSmsNotificationOff: "This contact has text message notifications turned off.",
	participantPhoneMissing: "This contact has no phone number saved.",
	sendSmsNotRequested: "Text message wasn't selected for this invitation.",
	organizationFormSmsDisabled: "Text messages are turned off for this form in your organization's settings.",
	formSubmissionSkip: "This form is set not to send an invitation when it's submitted.",
	// LSP-20787 (D22): a resend with no channel never re-texts a number whose invite text was refused.
	smsNumberBlocked: "We can't text this number, so it wasn't texted again.",
	unclassifiedCondition: "We couldn't send the text message. Please check the contact's phone number and notification settings."
};

const EMAIL_SKIP_MESSAGES: Record<string, string> = {
	// LSP-20787 (22024): the API reads the session participant's own email flag, not a contact-wide setting.
	participantEmailNotificationOff: "Email wasn't turned on for this session.",
	participantEmailMissing: "This contact has no email address saved.",
	sendEmailNotRequested: "Email wasn't selected for this invitation.",
	organizationEmailInvitationsDisabled: "Email invitations are turned off in your organization's settings."
};

/** Used for a reason the API added after this build shipped, so new codes still warn instead of reading as success. */
const UNKNOWN_MESSAGES: Record<InvitationChannel, string> = {
	sms: "We couldn't send the text message. Please check the contact's phone number and notification settings.",
	email: "We couldn't send the email. Please check the contact's email address and notification settings."
};

export interface BlockedInvitationChannel {
	channel: InvitationChannel;
	reason?: string;
	message: string;
}

export interface InvitationDeliverySummary {
	/** Channels the API reported as queued, in display order (SMS before email). */
	delivered: InvitationChannel[];
	/** Distinct blocked-channel explanations, in display order. */
	blocked: BlockedInvitationChannel[];
	/** Ready-to-show warning text, or null when every reported channel was queued. */
	warning: string | null;
}

function messageFor(channel: InvitationChannel, reason: string | undefined): string {
	const table = channel === "sms" ? SMS_SKIP_MESSAGES : EMAIL_SKIP_MESSAGES;
	if (!reason) return UNKNOWN_MESSAGES[channel];
	return table[reason] ?? UNKNOWN_MESSAGES[channel];
}

function joinChannelLabels(channels: InvitationChannel[]): string {
	const labels = channels.map((channel) => CHANNEL_LABELS[channel]);
	if (labels.length <= 1) return labels[0] ?? "";
	return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * Fold the API's per-participant dispositions into one agent-facing verdict.
 *
 * A channel is judged on `sms`/`email` being `"skipped"`, not on the presence of a reason string: the
 * status is the API's actual verdict, and a skip that arrives without a reason must still warn rather
 * than silently pass as sent.
 *
 * Duplicates are collapsed so a three-participant conversation that fails the same way three times
 * reads as one bullet.
 *
 * LSP-20787: a single-channel resend (`requestedChannel`) ignores the other channel, which the API reports
 * as skipped only because it was deliberately not requested (`sendSmsNotRequested` / `sendEmailNotRequested`).
 */
export function summarizeInvitationDelivery(
	invitations: InvitationDisposition[] | undefined,
	requestedChannel?: InvitationChannel
): InvitationDeliverySummary {
	const delivered = new Set<InvitationChannel>();
	const blocked: BlockedInvitationChannel[] = [];
	const seenMessages = new Set<string>();

	const consider = (channel: InvitationChannel, status: string | undefined, reason: string | undefined) => {
		if (requestedChannel && channel !== requestedChannel) return;
		if (status === "queued") {
			delivered.add(channel);
			return;
		}
		if (status !== "skipped") return;
		const message = messageFor(channel, reason);
		const key = `${channel}:${message}`;
		if (seenMessages.has(key)) return;
		seenMessages.add(key);
		blocked.push({ channel, reason, message });
	};

	for (const invitation of invitations ?? []) {
		consider("sms", invitation?.sms, invitation?.smsReason);
		consider("email", invitation?.email, invitation?.emailReason);
	}

	const deliveredChannels = (["sms", "email"] as InvitationChannel[]).filter((channel) => delivered.has(channel));

	if (blocked.length === 0) return { delivered: deliveredChannels, blocked, warning: null };

	const bullets = blocked.map((entry) => `• ${entry.message}`).join("\n");
	const blockedChannels = (["sms", "email"] as InvitationChannel[]).filter((channel) =>
		blocked.some((entry) => entry.channel === channel)
	);

	// A partial send must not read as a total failure — name what DID go out first.
	const heading =
		deliveredChannels.length > 0
			? `${joinChannelLabels(deliveredChannels)} sent. ${joinChannelLabels(blockedChannels)} could not be sent.`
			: "Invitation could not be sent";

	return { delivered: deliveredChannels, blocked, warning: `${heading}\n${bullets}` };
}

/** The two toast methods notifyInvitationDelivery uses, narrowed from vue-toastification's own interface. */
export type InvitationDeliveryToastHandler = Pick<ToastInterface, "success" | "warning">;

/**
 * LSP-20787: the one summarise-then-toast block every resend path shares, so they all read the same. A skipped
 * channel (other than the one deliberately not requested) shows the warning INSTEAD of the caller's success message.
 */
export function notifyInvitationDelivery(
	toast: InvitationDeliveryToastHandler,
	invitations: InvitationDisposition[] | undefined,
	successMessage: string,
	requestedChannel?: InvitationChannel
): void {
	const summary = summarizeInvitationDelivery(invitations, requestedChannel);
	if (summary.warning) {
		// pre-line so the bullets render as separate lines; align-top matches the taller toast.
		toast.warning(summary.warning, {
			timeout: calculateTimeout(summary.warning),
			toastClassName: ["toast-align-top", "toast-multiline"]
		});
		return;
	}
	toast.success(successMessage, { timeout: calculateTimeout(successMessage) });
}
